<template>
  <div class="case-dashboard" v-if="ecgData">
    <div class="dashboard-header">
      <div class="selectors">
        <!-- <multi-select-component
          class="trial-selector"
          label="Trial"
          mode="single"
          v-model="currentTrial"
          :options="availableTrials || []"
          :configurationOptions="{'allow-empty':false}"
        ></multi-select-component> -->
        <multi-select-component
          class="widget-selector"
          label="Output widgets"
          mode="multiple"
          v-model="widgetInputModel"
          :options="toOptions(widgetsForThisCase)"
        ></multi-select-component>
      </div>
      <div class="scalar-values-wrapper">
        <scalar-values-table
          :data="this.$store.state.paramsData.subject.scalars"
        ></scalar-values-table>
      </div>
      <div class="ecg-wrapper">
        <ECGChart
          ref="mainEgc"
          :data="ecgData"
          v-model="actualCurrentTime"
          :controls="true"
          :speeds="availableSpeeds"
          :currentPlaySpeed="currentPlaySpeed"
          :largeHandle="true"
          :showTooltip="true"
          :xAxis="false"
          @changePlayStatus="(s)=>currentPlayStatus=s"
          @changePlaySpeed="(s)=>currentPlaySpeed=s"
        ></ECGChart>
      </div>
    </div>
    <div class="scroll-wrapper">
      <grid-layout
        :layout="layout"
        :use-css-transforms="true"
        @layout-updated="saveLayout"
        :margin="[25,25]"
        :col-num="10"
        :row-height="60"
      >
        <grid-item
          v-for="item in layout"
            dragAllowFrom=".item-header"
            :x="item.x"
            :y="item.y"
            :w="item.w"
            :h="item.h"
            :i="item.i"
            :minW="item.minW || 3"
            :minH="item.minH || 3"
            :key="item.i"
          >
          <div class="item-header">
            <div class="title">{{getWidgetName(item.id)}}</div>
            <div class="sub-title">{{getWidgetSubName(item.id) || ('TIME: '+currentTimeString)}}</div>
            <div class="remove" @click="removeWidget(item)"></div>
          </div>
          <component
            :is="getComponentClass(item.id)"
            :currentTime="throttledCurrentTime"
            :actualCurrentTime="actualCurrentTime"
            @change-time="(t)=>actualCurrentTime=t"
            :currentCol="currentCol"
            @change-col="(col)=>currentCol=col"
            :currentSlice="currentSlice"
            @change-slice="(s)=>currentSlice=s"
            :currentSegment="currentSegment"
            @change-segment="(s)=>currentSegment=s"
            :currentPlayStatus="currentPlayStatus"
            :currentPlaySpeed="currentPlaySpeed"
            :totalECGTime="totalECGTime"
            v-bind="getComponentConfig(item.id)"
            :style="{flex: '1 1 auto', overflow: 'hidden', width: '100%'}"
          />
        </grid-item>
      </grid-layout>
    </div>
  </div>
  <loading-overlay v-else></loading-overlay>
</template>

<script>

const scenario_layout = {
  'CRT_LAYOUT': [
    { x: 0, y: 0, w: 3, h: 4, i: 4, id: 4, moved: false },
    { x: 3, y: 0, w: 7, h: 4, i: 5, id: 5, moved: false },
    { x: 3, y: 4, w: 7, h: 3, i: 6, id: 6, moved: false },
    { x: 3, y: 4, w: 10, h: 7, i: 5, id: 11, moved: false },
  ],
  'cardiosafe_LAYOUT': [
    { x: 3, y: 4, w: 5, h: 6, i: 6, id: 10, moved: false},
    { x: 0, y: 4, w: 10, h: 18, i: 7, id: 11, moved: false},
  ]
}

import { throttle } from 'throttle-debounce'
import ModelView from '@/components/CaseDashboard/3DModel'
import ECGChart from '@/components/CaseDashboard/ECGChart'
import ApolloAPI from '@/api/apollo_api'
// import MultiVideo from '@/components/CaseDashboard/MultiVideoGif'
import MultiVideo from '@/components/CaseDashboard/MultiVideoMp4'
import Bullseye from '@/components/CaseDashboard/Bullseye'
import LineChart from '@/components/CaseDashboard/LineChart'
import SegmentTable from '@/components/CaseDashboard/SegmentTable'
import ScalarValuesTable from '@/components/CaseDashboard/ScalarValuesTable'
import LinesGrid from '@/components/CaseDashboard/LinesGrid'
import VueGridLayout from 'vue-grid-layout'
// import DisplaySelector from '@/components/CaseDashboard/DisplaySelector'
import MultiSelectComponent from '@/components/MultiSelectComponent'
import { timeToString } from '@/utils'

export default {
  name: 'CaseDashboard',
  components: {
    ModelView,
    // VueSlider,
    MultiVideo,
    ECGChart,
    Bullseye,
    LineChart,
    SegmentTable,
    GridLayout: VueGridLayout.GridLayout,
    GridItem: VueGridLayout.GridItem,
    MultiSelectComponent,
    ScalarValuesTable,
    LinesGrid
  },
  data() {
    return {
      actualCurrentTime: 0,
      throttledCurrentTime: 0,
      dataReady: false,
      availableWidgets: [
        {
          id: 1,
          key: 'mesh',
          label: '3d model',
          subLabel: '$time',
          component: '3d',
          defaultSize: { w: 3, h: 5 },
          minW: 4,
          minH: 4,
          config: { variablesSrc: 'surf_vars.json', hdfSrc: 'o.zlib.h5' },
        },
        {
          id: 2,
          key: 'slices',
          label: 'Video Long Axis',
          subLabel: '$time',
          component: 'video',
          defaultSize: { w: 4, h: 5 },
          minW: 4,
          minH: 5,
          config: { direction: 'long' },
        },
        {
          id: 3,
          key: 'slices',
          label: 'Video Short Axis',
          subLabel: '$time',
          component: 'video',
          defaultSize: { w: 4, h: 5 },
          minW: 4,
          minH: 5,
          config: { direction: 'short' },
        },
        {
          id: 4,
          key: 'segments',
          label: 'Bullseye',
          subLabel: '$time',
          component: 'bullseye',
          defaultSize: { w: 4, h: 5 },
          minW: 4,
          minH: 4,
          config: null,
        },

        {
          id: 5,
          key: 'loops',
          label: 'Volume',
          subLabel: '$time',
          component: 'linechart',
          defaultSize: { w: 3, h: 7 },
          config: { dataSource: 'lines_json', lineId: 'time-volume', legend: 'Ventricle' },
        },
        {
          id: 6,
          key: 'loops',
          label: 'Pressure',
          subLabel: '$time',
          component: 'linechart',
          defaultSize: { w: 3, h: 7 },
          config: { dataSource: 'lines_json', lineId: 'time-pressure', legend: 'Ventricle' },
        },
        {
          id: 7,
          key: 'loops',
          label: 'Vol vs Pressure',
          subLabel: '$time',
          component: 'linechart',
          defaultSize: { w: 3, h: 7 },
          config: { dataSource: 'lines_json', lineId: 'volume-pressure', legend: 'Ventricle' },
        },
        {
          id: 8,
          key: 'segments',
          label: 'Segment values',
          subLabel: '$time',
          component: 'linechart',
          defaultSize: { w: 4, h: 7 },
          config: { dataSource: 'segments_json', lineId: 'time-dynamic', legend: 'Segments' },
        },
        {
          id: 9,
          key: 'segments',
          label: 'Segment Table',
          subLabel: '$time',
          component: 'segmenttable',
          defaultSize: { w: 6, h: 6 },
          config: null,
        },
        {
          id: 10,
          key: 'ecg',
          label: 'Calcium Concentration',
          subLabel: '$time',
          component: 'linechart',
          defaultSize: { w: 5, h: 6 },
          config: { dataSource: 'ecgline', lineId: 'time-Ca', legend: 'Channel', isSingleVarPlot: true},
        },
        {
          id: 11,
          key: 'ecg',
          label: 'Leads Grid View',
          subLabel: '$time',
          component: 'linesgrid',
          defaultSize: { w: 10, h: 18 },
        },
      ],
      currentTrial: null,
      layout: [],
      widgetInputModel: [],
      currentCol: null, //FEo este "hardcoding"...
      currentSlice: null,
      currentSegment: null,
      currentPlayStatus: 'paused',
      currentPlaySpeed: 0.5
    }
  },
  mounted() {
    this.loadLayout()
  },
  asyncComputed: {
    ecgData() {
      if (
        this.$store.state.paramsData.project &&
        this.$store.state.paramsData.trial &&
        this.$store.state.paramsData.subject
      ) {
        return ApolloAPI.getEcg(this.$store.state.paramsData)
      }
    },
    widgetsForThisCase() {
      let pms = this.$store.state.paramsData
      if (pms.subject) {
        return this.availableWidgets.filter(w => pms.subject.available_outputs.indexOf(w.key) >= 0)
      }
    },
    // availableTrials () {
    //   let project = this.$store.state.paramsData.project
    //   if(!project) return
    //   return Api.getProjectResults(project.id).then((ress)=>{
    //     let trialMetas = ress.filter((s)=>s.subject.templateId===this.$store.state.paramsData.subject.subject.templateId).map((s)=>s.trialMeta)
    //     return trialMetas.map((tm)=>tm.name)
    //   })
    // }
  },
  computed: {
    activeWidgets() {
      if (this.widgetInputModel) { return this.widgetInputModel.map(v => v.id) }  //.map((v,i)=>v && this.availableWidgets[i].id).filter((v)=>v!==false)
      else return []
    },
    currentTimeString() {
      return timeToString(this.actualCurrentTime)
    },
    totalECGTime() {
      return this.ecgData ? this.ecgData[this.ecgData.length - 1].T : 0
    },
    availableSpeeds() {
      let hasVideo = this.availableWidgets
        .filter(a => this.activeWidgets.indexOf(a.id) >= 0)
        .find(w => w.key === 'slices')
      let has3DModel = this.availableWidgets
        .filter(a => this.activeWidgets.indexOf(a.id) >= 0)
        .find(w => w.key === 'mesh')
      if (hasVideo || has3DModel) return [0.1, 0.05]
      else return [0.5, 0.1, 0.05]
    },
  },
  watch: {
    // actualCurrentTime: throttle(35,function(){
    //   this.throttledCurrentTime = this.actualCurrentTime
    // }),
    actualCurrentTime() {
      this.throttledCurrentTime = this.actualCurrentTime
    },
    activeWidgets() {
      this.layout.forEach(wl => {
        let candidate = this.activeWidgets.find(wi => wi === wl.id)
        if (!candidate) this.layout = this.layout.filter(ll => ll !== wl)
      })
      this.activeWidgets.forEach(wid => {
        let candidate = this.layout.find(l => l.id === wid)
        let def = this.availableWidgets.find(w => w.id === wid)
        if (!candidate)
          this.layout.push({
            ...this.getBestForNew(def.defaultSize.w, def.defaultSize.h),
            minH: def.minH,
            minW: def.minW,
            i: wid,
            id: wid,
          })
      })
      this.saveLayout()
      //this.$refs.mainEgc && this.$refs.mainEgc.pause()
    },
    availableSpeeds() {
      if (this.availableSpeeds.indexOf(this.currentPlaySpeed) < 0)
        this.currentPlaySpeed = this.availableSpeeds[0]
    }
  },
  methods: {
    skip() {
      return Math.floor(this.ecgData.length/1500) || 1
    },
    extractLeadData(widgetId) {
      let leadName = this.getComponentConfig(widgetId).config.lineId.split('-')[1]
      return this.ecgData.filter((d,i)=>i%this.skip()===0).map((d)=>({dt:d['Time'],value:d[leadName]}))
    },
    toOptions(arr) {
      return arr.map(v => {
        return {
          label: v.label,
          value: v,
        }
      })
    },
    removeWidget(widget) {
      this.widgetInputModel = this.widgetInputModel.filter(aw => aw.id !== widget.id)
    },
    getBestForNew(width, height) {
      // Versi es suficiente...
      let maxY = this.layout.reduce((maxY, d) => {
        return Math.max(maxY, d.y + d.h)
      }, 0)
      let maxX = this.layout.reduce((maxX, d) => {
        // return Math.max(maxX,d.x+d.w)
        if (d.y + d.h >= maxY) return Math.max(maxX, d.x + d.w)
        else return maxX
      }, 0)
      if (maxX + width > 10) {
        maxX = 0
        maxY += height
      }
      return { x: maxX, y: maxY, w: width, h: height }
    },
    saveLayout() {
      const pid = this.$route.params.project
      localStorage.setItem(`layout_${pid}`, JSON.stringify(this.layout))
    },
    loadLayout() {
      const pid = this.$route.params.project
      this.layout = localStorage.getItem(`layout_${pid}`) != null ? JSON.parse(localStorage.getItem(`layout_${pid}`)) : scenario_layout[`${this.$store.state.paramsData.project.scenario}_LAYOUT`]
      this.widgetInputModel = []
      this.layout.forEach(l => {
        let widgetDef = this.availableWidgets.find(aw => aw.id === l.id)
        this.widgetInputModel.push(widgetDef)
      })
    },
    getWidgetName(widgetId) {
      return this.availableWidgets.find(w => w.id === widgetId).label
    },
    getWidgetSubName(widgetId) {
      let val = this.availableWidgets.find(w => w.id === widgetId).subLabel
      return val === '$time' ? null : val
    },
    getComponentConfig(widgetId) {
      let config = this.availableWidgets.find(w => w.id === widgetId).config
      return config ? { config } : null
    },
    getComponentClass(widgetId) {
      let component = this.availableWidgets.find(w => w.id === widgetId).component
      switch (component) {
        case '3d':
          return ModelView
        case 'video':
          return MultiVideo
        case 'bullseye':
          return Bullseye
        case 'linechart':
          return LineChart
        case 'segmenttable':
          return SegmentTable
        case 'linesgrid':
          return LinesGrid
      }
    },
  }
}
</script>

<style scoped lang="stylus">
.case-dashboard
  display: flex
  flex-direction: column
  width: 100%
  height: 100%

  .scroll-wrapper
    flex: 1 1 0%
    overflow-y: auto

    .vue-grid-layout
      flex: 1 1 0%

      >>> .vue-grid-placeholder
        border: dashed 1px #00f
        background: #fff
        transition: transform 0.2s

      .vue-grid-item
        display: flex
        flex-direction: column
        padding: 20px 15px
        background-color: #fff
        box-shadow: 0 3px 6px rgba(#000, 0.15)

        &.vue-draggable-dragging
          box-shadow: 0 3px 6px rgba(#000, 0.5)

        .item-header
          position: relative
          display: flex
          flex-direction: column
          margin-bottom: 10px
          background-color: #fff

          .title
            flex: 1 1 0%
            font-weight: $fw-semi-bold
            m-font-size(18, 20)
            margin-bottom: 5px

          .sub-title
            font-weight: $fw-light
            m-font-size(12, 14)
            text-transform: uppercase

          .remove
            height: 15px
            cursor: pointer
            m-icon("cross", 15)
            position: absolute
            top: -5px
            right: 0
            z-index: 99
            width: @height

        >>> .vue-resizable-handle
          $padding = 5px
          bottom: 15px - $padding
          background-color: rgba(#fff, 0.75)
          m-icon("arrow-diagonal-down-right", 20 + $padding)
          right: 15px - $padding
          padding: $padding

  .dashboard-header
    position: relative
    display: flex
    flex-wrap: wrap
    align-items: center
    padding-right: 25px
    padding-left: 25px
    height: 250px
    border-bottom: 2px solid $titan-white
    background: $tuna
    // background: linear-gradient(90deg, $tuna 0, $comet 100%)
    background: none

    .selectors
      display: flex
      margin-right: 20px
      padding: 10px
      height: 70px
      width: 20%
      border-radius: 0 0 5px 5px
      border-radius: 4px
      background: $titan-white

      .multi-select-component
        position: relative

        // >>>.label
        // color: #fff
        &.trial-selector
          width: 120px

        &.widget-selector
          width: 200px

          >>> .multiselect__content-wrapper
            max-height: none !important

    .scalar-values-wrapper
      flex: 1 1 50%
      height: 70px
      width: 80%
      border-radius: 4px
      background-color: #fff

    .ecg-wrapper
      height: 130px
      width: 100%
      background-color: #fff
      box-shadow: 0 3px 6px rgba(#000, 0.15)
</style>